// VersionBanner — B-1b (MONIT 2026-09-12_1750, F3): mechanizm „nowa wersja, przeładuj",
// którego aplikacja nigdy nie miała. Renderowany w App.jsx obok OfflineBanner na każdej gałęzi
// (login, Serwis, lista admina, trasy).
//   (b) „🔄 Nowa wersja gotowa" + „Przeładuj" + ✕ (wraca po 1 h, DN3 = a)   data-banner="version-ready"
//   (c) „⬇ Pobieram nową wersję n/N, dokończy się przy zasięgu" (cichy)     data-banner="version-downloading"
//   (e) „⚠ Wymagana aktualizacja … dokończ przy zasięgu. Możesz pracować."  data-banner="version-required"
//   (d) pełnoekranowa blokada z JEDNYM przyciskiem „Przeładuj" (portal, z-index 3000; pułapka
//       z-index overlayów: nowe overlaye ZAWSZE przez createPortal)            data-banner="version-block"
// Zasada twarda: (d) TYLKO gdy nowa wersja jest w komplecie w telefonie (evaluateVersionState).

import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { subscribeVersionState, getVersionState, dismissBanner, isBannerHidden, reloadForNewVersion } from '../../services/versionCheck.js';
import { versionStateText } from '../../shared/appVersion.js';
import { COLORS, FONTS } from '../../shared/tokens.js';
import { useLang } from '../../hooks/useLang.js';

const BLUE_BG = 'rgba(59,130,246,0.16)';
const BLUE_BORDER = 'rgba(59,130,246,0.7)';
const YELLOW = 'var(--fo-yellow, #eab308)';

export function useVersionState() {
  const [v, setV] = useState(() => getVersionState());
  useEffect(() => subscribeVersionState(setV), []);
  return v;
}

export default function VersionBanner() {
  const { t } = useLang();
  const v = useVersionState();
  const [, setTick] = useState(0);
  // Zamknięty baner (b) wraca po 1 h — tykanie co minutę.
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 60_000);
    return () => clearInterval(t);
  }, []);
  const [reloading, setReloading] = useState(false);
  const reload = useCallback(async () => {
    setReloading(true);
    await reloadForNewVersion();
    setTimeout(() => setReloading(false), 5000);
  }, []);

  if (!v || v.state === 'a') return null;
  const text = versionStateText(v);

  if (v.state === 'd') {
    const overlay = (
      <div role="dialog" aria-modal="true" data-banner="version-block" style={{
        position: 'fixed', inset: 0, zIndex: 3000,
        background: 'rgba(16,16,18,0.97)', color: '#fff',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: 24, fontFamily: FONTS.body, textAlign: 'center',
      }}>
        <div style={{ fontSize: 44, marginBottom: 12 }} aria-hidden="true">🔄</div>
        <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>{t('vb.requiredTitle', { t: v.target ? ` ${v.target}` : '' })}</div>
        <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.75)', maxWidth: 320, lineHeight: 1.45, marginBottom: 24 }}>
          {t('vb.requiredBody')}
        </div>
        <button onClick={reload} disabled={reloading} data-version-reload="1" style={{
          width: '100%', maxWidth: 320, padding: '16px 20px', borderRadius: 14,
          background: YELLOW, color: '#111', border: 'none', fontSize: 17, fontWeight: 800,
          cursor: 'pointer', fontFamily: FONTS.body, WebkitTapHighlightColor: 'transparent',
          opacity: reloading ? 0.6 : 1,
        }}>{reloading ? t('vb.reloading') : t('errb.reload')}</button>
        <div style={{ marginTop: 16, fontSize: 11, color: 'rgba(255,255,255,0.45)', fontFamily: FONTS.mono }}>
          {t('vb.haveRequired', { shell: v.shell, min: v.minRequired })}
        </div>
      </div>
    );
    return typeof document !== 'undefined' ? createPortal(overlay, document.body) : overlay;
  }

  if (v.state === 'b') {
    if (isBannerHidden()) return null;
    return (
      <div role="status" data-banner="version-ready" style={{
        padding: '8px 12px', fontSize: 13, background: BLUE_BG, borderBottom: `1px solid ${BLUE_BORDER}`,
        position: 'sticky', top: 0, zIndex: 100, color: COLORS.text, fontFamily: FONTS.body,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flexWrap: 'wrap',
      }}>
        <span style={{ flex: '0 1 auto' }}>{text}</span>
        <button onClick={reload} disabled={reloading} data-version-reload="1" style={{
          padding: '6px 12px', borderRadius: 999, background: '#3b82f6', color: '#fff', border: 'none',
          fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: FONTS.body, WebkitTapHighlightColor: 'transparent',
        }}>{reloading ? '…' : t('errb.reload')}</button>
        <button onClick={() => dismissBanner()} aria-label={t('vb.dismissAria')} data-version-dismiss="1" style={{
          background: 'transparent', border: 'none', color: COLORS.textMuted, fontSize: 16, cursor: 'pointer',
          padding: '2px 6px', WebkitTapHighlightColor: 'transparent',
        }}>✕</button>
      </div>
    );
  }

  // (c) / (e): ciche banery informacyjne, praca dozwolona.
  const strong = v.state === 'e';
  return (
    <div role="status" data-banner={strong ? 'version-required' : 'version-downloading'} style={{
      padding: '5px 12px', fontSize: 11.5,
      background: strong ? 'rgba(234,179,8,0.16)' : 'rgba(255,255,255,0.05)',
      borderBottom: `1px solid ${strong ? YELLOW : 'rgba(255,255,255,0.12)'}`,
      position: 'sticky', top: 0, zIndex: 99, color: strong ? YELLOW : COLORS.textMuted,
      fontFamily: FONTS.mono, textAlign: 'center',
    }}>
      {text}
    </div>
  );
}
